import { useEffect, useReducer } from 'react'
import { getSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { addApolloState, initializeApollo } from '@/api/apollo'
import {
  SearchJobsQuery,
  SearchJobsQueryVariables,
  SearchJobsDocument,
  Job,
  JobsLocationsDocument,
  JobsLocationsQuery,
  JobsLocationsQueryVariables,
  LocaleEnum,
  MetadataQuery,
  MetadataQueryVariables,
  MetadataDocument,
  PageEnum,
  Metadata,
  MeQuery,
  MeQueryVariables,
  MeDocument,
  Candidate,
} from '../../../generated/apolloComponents'
import {
  JobListContext,
  jobListReducer,
  convertJobStateFiltersToQueryFilters,
} from '@/context/jobListContext'
import JobList from '@/components/templates/JobsList'
import { convertSlugToState } from '@/helpers/filters'
import CustomHead from '@/components/molecules/CustomHead'
import { arrayOfObjectToStringArrayByKey } from '@/helpers/utils'
import { useAnalyticsPush } from '@/context/analyticsContext'
import { ScrollRestorer } from '@/components/atoms/ScrollRestorer'
import { countryFromHeader } from '@/helpers/url'

interface iProps {
  jobs: Job[]
  jobsLocations: string[]
  totalCount: number
  slugState: any
  metaData: Metadata
  country: string
}

const Jobs = ({
  jobs,
  jobsLocations,
  totalCount,
  slugState,
  metaData,
  country,
}: iProps) => {
  const [state, dispatch] = useReducer(jobListReducer, slugState)
  const { pushEvent } = useAnalyticsPush()
  const { lang } = useTranslation()
  const noindex = !!(slugState.order && slugState.order.value)

  useEffect(() => {
    const data = { ...slugState?.filters }

    Object.keys(data).forEach((key) => {
      const value = data[key]
      if (Array.isArray(value) && value[0] instanceof Object) {
        data[key] = arrayOfObjectToStringArrayByKey(data[key], 'value')
      }
    })

    if (window) {
      pushEvent({
        event: 'search',
        searchData: {
          type: 'job',
          results: totalCount,
          ...data,
        },
      })
    }
  }, [jobs])

  return (
    <>
      <CustomHead metaData={metaData} noindex={noindex} />
      <JobListContext.Provider value={{ state, dispatch }}>
        <ScrollRestorer />
        <JobList
          jobs={jobs}
          jobsLocations={jobsLocations}
          totalCount={totalCount}
          slugState={slugState}
          metaData={metaData}
          domainLang={lang}
          country={country}
        />
      </JobListContext.Provider>
    </>
  )
}

export const getServerSideProps = async (context: any) => {
  const page: number = 1
  const perPage: number = 50
  const apolloClient = initializeApollo({ ctx: context })

  const stateBySlug = convertSlugToState(context.query.slug, { page, perPage })
  const queryByState = convertJobStateFiltersToQueryFilters(stateBySlug.filters)

  const { data: me } = await apolloClient.query<MeQuery, MeQueryVariables>({
    query: MeDocument,
  })
  const person = me.me as Candidate

  const variables: SearchJobsQueryVariables = {
    page: stateBySlug.page,
    perPage: perPage,
    filters: queryByState,
    language: LocaleEnum[context.locale as 'pl' | 'en'],
    country: countryFromHeader(context),
  }

  if (stateBySlug.order) {
    variables.order = stateBySlug.order.value
  }

  if (person && person.rejectedJobs)
    variables.exclude = [...person.rejectedJobs]

  const queries: any = []

  const jobsDataQuery = apolloClient.query<
    SearchJobsQuery,
    SearchJobsQueryVariables
  >({
    query: SearchJobsDocument,
    variables: variables,
  })
  queries.push(jobsDataQuery)

  const jobsLocationsDataQuery = apolloClient.query<
    JobsLocationsQuery,
    JobsLocationsQueryVariables
  >({
    query: JobsLocationsDocument,
    variables: {
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      country: countryFromHeader(context),
    },
  })
  queries.push(jobsLocationsDataQuery)

  const metaDataQuery = apolloClient.query<
    MetadataQuery,
    MetadataQueryVariables
  >({
    query: MetadataDocument,
    variables: {
      pageType: PageEnum.JobList,
      language: LocaleEnum[context.locale as 'pl' | 'en'],
      jobFilters: queryByState,
    },
  })
  queries.push(metaDataQuery)

  const [jobsData, jobsLocationsData, metaData]: any = await Promise.all(
    queries
  )

  return addApolloState(apolloClient, {
    props: {
      session: await getSession(context),
      metaData: metaData.data.metadata,
      slugState: stateBySlug,
      totalCount: jobsData.data.searchJobs.totalCount,
      jobs: jobsData.data.searchJobs.nodes,
      jobsLocations: jobsLocationsData.data.jobsLocations,
      country: countryFromHeader(context),
    },
  })
}

export default Jobs
